import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
} from '@angular/core';

import {
  DEFAULT_EXCLUDE_FALSY_TOOGLE,
  Dimension,
  Result,
  DateRangeValue,
  ColorService,
  EMPTY_FALSY_ERROR,
  MessageService,
  TrackingService,
  DatesService,
  DynamicDateRange,
} from '@hub-components';

import {
  DEFAULT_DIMENSION_LABEL,
  DEFAULT_DIMENSION_VALUE_RESULTS,
  DEFAULT_DIMENSION_VALUE,
  DEFAULT_INCLUDE_DIMENSIONS_TOGGLE,
} from '../../../../constants';

import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { DEFAULT_STYLE_OPTIONS } from '../default-chart-options';

const SUPPORTED_INPUTS = [
  {
    bindTo: 'dimensions',
    label: 'Dimension',
    type: 'dimension',
  },
];

interface DimensionValueChartOptions extends ChartOptions {
  icons: { [key: string]: string },
  baseColor: string,
  backgroundColor: string,
  dimensionLabel: { enabled: boolean, align: string, size: number },
  dimensionValue: { size: number, align: string },
  dateRange: { enabled: boolean, value: DateRangeValue }
}

@Component({
  selector: 'dimension-value-chart',
  templateUrl: './dimension-value.component.html',
  styleUrls: ['./dimension-value.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DimensionValueComponent implements OnChanges {
  @HostBinding('style.backgroundColor') backgroundColor: string;

  @Input() result: Result;
  @Input() dimensions: Dimension[];
  @Input() style: WidgetStyle;
  @Input() chartOptions: DimensionValueChartOptions;
  @Input() dashboardInterval: DynamicDateRange;

  static supportedInputs = SUPPORTED_INPUTS;
  static supportedOptions = [
    {
      bindTo: 'resultsPerPage',
      label: 'Number of Results',
      type: 'resultsPerPageDropdown',
    }, {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'includeDimensions',
        }, {
          bindTo: 'excludeFalsy',
        },
      ],
    }, {
      bindTo: 'title',
      label: 'Title',
      type: 'textFormat',
      options: [
        {
          bindTo: 'text',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    }, {
      bindTo: 'dimensionValue',
      label: 'Dimension',
      type: 'textFormat',
      options: [
        {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    }, {
      bindTo: 'dimensionLabel',
      label: 'Dimension Label',
      type: 'textFormat',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    }, {
      bindTo: 'footer',
      label: 'Footer',
      type: 'footerCheckbox',
      options: [
        {
          bindTo: 'datasetInfo',
        },
        {
          bindTo: 'dateRange',
        },
      ],
    }, {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = [
    {
      bindTo: 'backgroundColor',
      label: 'Background Color',
    },
    {
      bindTo: 'baseColor',
      label: 'Dimension Color',
    },
    {
      bindTo: 'textColor',
      label: 'Text Color',
    },
    {
      bindTo: 'borderWidth',
      label: 'Thickness',
    },
    {
      bindTo: 'shadow',
      label: 'Size',
    },
    {
      bindTo: 'shadowColor',
      label: 'Color',
    },
    {
      bindTo: 'shadowOpacity',
      label: 'Opacity',
    },
    {
      bindTo: 'borderColor',
      label: 'Color',
    },
    {
      bindTo: 'borderRadius',
      label: 'Corner Radius',
    },
  ];

  static supportedRollups = ['total'];
  static supportedResultsPerPage = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  public dimensionValues: any[];
  private _dimensionValues: any[] = [];
  private _filteredDimensionValues: any[] = [];
  private isFilterEmpty = false;
  dimensionColor: string;
  dimensionLabel: { enabled: boolean, align: string, size: number } = DEFAULT_DIMENSION_LABEL;
  textColor: string;
  dimensionValue: { size: number, align: string } = DEFAULT_DIMENSION_VALUE;
  title: { [key: string]: string | number };
  footer: any;
  public footerData: any;
  resultCount = DEFAULT_DIMENSION_VALUE_RESULTS;
  includeDimensions = DEFAULT_INCLUDE_DIMENSIONS_TOGGLE;
  private excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;

  icons: { [key: string]: string };

  datasetDisplayName: string;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private colorService: ColorService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private datesService: DatesService,
  ) { }

  ngOnChanges() {
    if (this.style) {
      this.backgroundColor = this.style.backgroundColor;
      this.dimensionColor = this.style.baseColor;
      this.textColor = this.style.textColor;
    }

    if (this.chartOptions) {
      if (this.chartOptions.dataOptions) {
        if (!this.excludeFalsy && this.isFilterEmpty && this.chartOptions.dataOptions.excludeFalsy) {
          this.messageService.info(EMPTY_FALSY_ERROR);
        }
        if (this.excludeFalsy !== this.chartOptions.dataOptions.excludeFalsy) {
          this.addTracking('excludeFalsy');
          this.excludeFalsy = this.chartOptions.dataOptions?.excludeFalsy;
        }
        this.includeDimensions = this.chartOptions.dataOptions.includeDimensions;
        this.updateSeriesData();
      }

      if (this.chartOptions.footer) {
        if (this.footer !== undefined && this.footer.dateRange !== this.chartOptions.footer?.dateRange) {
          this.addTracking('footerDateRange');
        }

        this.footer = this.chartOptions.footer;
        this.footerData = {
          datasetInfo: this.result?.query?.schema?.displayName,
          dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result?.query?.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
        };
      }

      if (this.chartOptions.resultsPerPage) {
        this.resultCount = this.chartOptions.resultsPerPage.value;
      }
      if (this.chartOptions.title) {
        this.title = this.chartOptions.title;
        this.title.align = this.chartOptions.title.align || 'center';
      }
      if (this.chartOptions.dimensionValue) {
        this.dimensionValue = this.chartOptions.dimensionValue;
      }
      if (this.chartOptions.dimensionLabel) {
        this.dimensionLabel = this.chartOptions.dimensionLabel;
      }
      this.icons = this.chartOptions.icons;
    }

    if (this.result && this.dimensions) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName;
      this._dimensionValues = this.getSeriesData(false);
      this._filteredDimensionValues = this.getSeriesData(true);
      this.isFilterEmpty = this._filteredDimensionValues.length === 0 && this._dimensionValues.length !== 0;
      if (this.isFilterEmpty && this.excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateSeriesData();
    }
  }

  private getSeriesData(excludeFalsy: boolean): any[] {
    const dataArr = [];
    this.result.rows
      .filter(row => row.metrics.some(metric => excludeFalsy ? !!metric.value : true))
      .forEach(row => {
        row.slices.forEach(d => {
          if (this.includeDimensions) {
            dataArr.push({ name: d.dimension.displayName, value: d.value, format: d.format });
          }
          if (d.properties) {
            d.properties.forEach(prop => {
              dataArr.push({ name: prop['name'], value: prop['value'], format: prop['format'] });
            });
          }
        });
      });

    return dataArr;
  }

  private updateSeriesData() {
    this.dimensionValues = this.excludeFalsy ? this._filteredDimensionValues.slice(0, this.resultCount) : this._dimensionValues.slice(0, this.resultCount);
  }

  private addTracking(type: string) {
    if (type === 'excludeFalsy') {
      this.trackingService.track('Dimension Value: Exclude N/A and 0 values', { value: this.chartOptions.dataOptions.excludeFalsy });
    }

    if (type === 'footerDateRange') {
      this.trackingService.track('Dimension Value: Footer DateRange', { value: this.chartOptions.footer.dateRange });
    }
  }
}
